import React from 'react';
import { ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer, Tooltip, LabelList, Line } from 'recharts';
import CustomTooltip from "../../../utils/CustomTooltip";
import styles from "./barAndLineComboChart.module.css";

const BarAndLineChartAlternateVendor = ({ data }) => {

    const transformedData = data?.map(item => {
        const otifRaw = item["OTIF%"];
        const qciRaw = item["QCI%"];
        const lt = item["Lead Time"];

        const otifpPercent = otifRaw === '-' || otifRaw === 'N/A'
            ? 0
            : parseFloat(otifRaw);

        const qualityPerformanceIndex = qciRaw === '-' || qciRaw === 'N/A'
            ? 0
            : parseFloat(qciRaw);

        const averageLT = lt === '-' || lt === 'N/A'
            ? 0
            : lt;

        return {
            vendorName: item["Vendor name"],
            otifpPercent,
            qualityPerformanceIndex,
            averageLT,
            vendorCode: item["Vendor code"],
        };
    });

    // Check if we should hide labels (when data length > 10)
    const shouldHideLabels = data?.length > 15;

    const CustomLegend = (props) => {
        const { payload } = props;
        return (
            <div style={{ display: 'flex', justifyContent: 'left', padding: '10px' }}>
                {payload.map((entry, index) => {
                    const { value, color } = entry;
                    return (
                        <span
                            key={`item-${index}`}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '0 10px',
                            }}
                        >
                            <span
                                style={{
                                    backgroundColor: color,
                                    width: '6px',
                                    height: '12px',
                                    display: 'inline-block',
                                    borderRadius: '2px',
                                }}
                            />
                            <span style={{ color: '#4D4D4F' }}>{value}</span>
                        </span>
                    );
                })}
            </div>
        );
    };

    const legendStyle = {
        fontSize: '10px',
        paddingTop: '15px',
        color: '#4D4D4F',
    };

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const vendor = payload[0].payload;
            return (
                <div className={styles.customTooltip}>
                    <p className={styles.tooltipItem} style={{ fontWeight: 500 }}>
                        Vendor name: {vendor.vendorName}
                    </p>
                    <p className={styles.tooltipItem} style={{ fontWeight: 500 }}>
                        Vendor code: {vendor.vendorCode}
                    </p>
                    {payload.map((item, index) => {
                        const valueWithSuffix =
                            item.name === "OTIF %" || item.name === "QCI %"
                                ? `${item.value}%`
                                : item.value;
                        return (
                            <p key={index} className={styles.tooltipItem}>
                                <span style={{ fontWeight: 500 }}>{item.name}</span> : {valueWithSuffix}
                            </p>
                        );
                    })}
                </div>
            );
        }
        return null;
    };

    // Custom tick with ellipsis + tooltip
    const CustomizedAxisTick = ({ x, y, payload, dataLength }) => {
        const text = payload.value;

        let maxChars = 4;
        if (dataLength >= 30) {
            maxChars = 2;
        } else if (dataLength >= 15) {
            maxChars = 3;
        }
        else if (dataLength <= 5) {
            maxChars = text.length;
        }

        return (
            <g transform={`translate(${x},${y})`}>
                <text
                    x={0}
                    y={0}
                    dy={20}
                    textAnchor="middle"
                    style={{
                        fill: "#706a6a",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontWeight: 600,
                        cursor: "pointer",
                    }}
                >
                    <title>{text}</title>
                    {text.length > maxChars ? `${text.substring(0, maxChars)}...` : text}
                </text>
            </g>
        );
    };


    return (
        <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={transformedData}
                margin={{
                    top: 20,
                    right: 0,
                    bottom: 10,
                    left: 0,
                }} >
                <CartesianGrid strokeDasharray="3 3" vertical={false} horizontal={true} />
                <YAxis domain={[0, 'auto']} tickLine={false} axisLine={false} className={styles.BarLabel} />
                <YAxis yAxisId="right" orientation="right" tickLine={false} axisLine={false} className={styles.BarLabel} />
                <XAxis dataKey="vendorName" align='center' tickLine={false} axisLine={false} className={styles.BarLabel} padding={{ left: 0, right: 0 }} interval={0} tick={<CustomizedAxisTick dataLength={transformedData?.length} />}>
                </XAxis>

                <Tooltip
                    content={<CustomTooltip />}
                    wrapperStyle={{
                        fontSize: '12px',
                        padding: '6px',
                    }}
                />
                <Legend verticalAlign="bottom" align="left" wrapperStyle={legendStyle} content={<CustomLegend />} />

                <Bar
                    radius={[4, 4, 0, 0]}
                    dataKey="otifpPercent"
                    name="OTIF %"
                    fill="#005EEF"
                    barSize={25}
                >
                    <LabelList
                        dataKey="otifpPercent"
                        position="top"
                        style={{ fill: '#2B2B2B', fontSize: 10, fontWeight: "500" }}
                        formatter={(value) => (value === 0 || shouldHideLabels) ? '' : `${value}%`}
                    />
                </Bar>
                <Bar
                    dataKey="qualityPerformanceIndex"
                    name="QCI %"
                    fill="#00B190"
                    barSize={25}
                    radius={[4, 4, 0, 0]}
                >
                    <LabelList
                        dataKey="qualityPerformanceIndex"
                        position="top"
                        style={{ fill: '#2B2B2B', fontSize: 10, fontWeight: "500" }}
                        formatter={(value) => (value === 0 || shouldHideLabels) ? '' : `${value}%`}
                    />
                </Bar>
                <Line
                    type="monotone"
                    dataKey="averageLT"
                    name="Lead Time"
                    stroke="#FF544F"
                    fill="#FF544F"
                    strokeWidth={2}
                    yAxisId="right"
                />
            </ComposedChart>
        </ResponsiveContainer>
    );
};

export default BarAndLineChartAlternateVendor;


